import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  MessageSquare,
  Image as ImageIcon,
  FileText,
  Layout,
  List,
  CheckCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  Loader,
  AlertCircle,
  RefreshCw,
  XCircle,
  Pause,
  Copy,
  CloudDownload,
  StopCircle,
  Ban,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getTemplates,
  deleteTemplate,
  updateTemplate,
  refreshTemplateStatus,
  getTemplateById,
  syncTemplatesFromMeta,
} from "../apis/template";
import ViewEditTemplateModal from "../components/ViewEditTemplateModal";

const Templates = () => {
  const [userData, setUserData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLanguage, setFilterLanguage] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalTemplates, setTotalTemplates] = useState(0);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const parsed = JSON.parse(user);
      setUserData(parsed);
    }
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterLanguage, filterCategory, searchTerm]);

  // Fetch templates when page, items per page, or filters change
  useEffect(() => {
    fetchTemplates(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filterStatus, filterLanguage, filterCategory, searchTerm]);

  // Helper function to extract display data from template for table view
  const getTemplateDisplayData = (template) => {
    const templateJson = template.template_json || {};
    const components = templateJson.components || [];
    
    // Extract basic info for display
    const bodyComponent = components.find(c => c.type === 'BODY');
    const headerComponent = components.find(c => c.type === 'HEADER');
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    
    const body = bodyComponent?.text || '';
    const headerType = headerComponent?.format?.toLowerCase() || 'none';
    const rawButtons = buttonsComponent?.buttons || [];
    
    // Determine template type for icon display
    let templateType = 'text';
    if (rawButtons.length > 0) {
      templateType = 'buttons';
    } else if (headerType !== 'none' && headerType !== 'text') {
      templateType = headerType;
    }
    
    return {
      ...template,
      body,
      template_type: templateType,
      header_type: headerType,
      meta_template_id: template.template_id || template.meta_template_id,
    };
  };

  const fetchTemplates = async (page = currentPage, perPage = itemsPerPage) => {
    setIsLoading(true);
    setError("");
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const wabaAccount = JSON.parse(
        localStorage.getItem("selectedWabaAccount") || "{}"
      );
      const wabaId = wabaAccount?.wabaId;
      
      // Calculate offset based on page number
      const offset = (page - 1) * perPage;
      
      // Prepare filters for backend
      const filters = {
        wabaId,
        limit: perPage,
        offset,
      };
      
      // Add filters if not "all"
      if (filterStatus !== "all") {
        filters.status = filterStatus;
      }
      if (filterLanguage !== "all") {
        filters.language = filterLanguage;
      }
      if (filterCategory !== "all") {
        filters.category = filterCategory;
      }
      if (searchTerm.trim()) {
        filters.searchTerm = searchTerm.trim();
      }
      
      const response = await getTemplates(authToken, filters);
      
      // Get templates and add display data only
      const rawTemplates = response.data.metaTemplates || [];
      const templatesWithDisplay = rawTemplates.map(getTemplateDisplayData);
      
      // Get total count from backend
      const total = response.data.total || 0;
      setTotalTemplates(total);
      
      // Get counts from backend response (array format: [{status: 'APPROVED', count: 10}, ...])
      const backendCounts = response.data.counts || [];
      const countsObj = {
        approved: 0,
        pending: 0,
        rejected: 0
      };
      
      // Transform backend counts array to object format
      backendCounts.forEach((item) => {
        const status = item.status?.toLowerCase();
        if (status && countsObj.hasOwnProperty(status)) {
          countsObj[status] = item.count || 0;
        }
      });

      setTemplates(templatesWithDisplay);
      setCounts(countsObj);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError(err.response?.data?.error || "Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh template status from Meta
  const handleRefreshStatus = async (template) => {
    if (!template.template_id) {
      alert("This template hasn't been submitted to Meta yet.");
      return;
    }

    setRefreshingId(template.id);
    setError("");

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await refreshTemplateStatus(authToken, template.id);

      if (response.data.success) {
        // Update local state
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === template.id
              ? {
                  ...t,
                  status: response.data.status,
                  rejected_reason: response.data.rejectedReason || null,
                }
              : t
          )
        );

        // Show success message
        const statusMessage =
          response.data.status?.toLowerCase() === "approved"
            ? "✅ Template approved by Meta!"
            : response.data.status === "rejected"
            ? "❌ Template rejected by Meta"
            : `Status updated to: ${response.data.status}`;

        alert(statusMessage);

        // Refresh counts
        await fetchTemplates();
      }
    } catch (err) {
      console.error("Error refreshing status:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to refresh template status";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleViewEdit = async (template) => {
    // Fetch full template details for view/edit modal
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await getTemplateById(authToken, template.id);
      const fullTemplate = response.data.template || template;
      setSelectedTemplate(fullTemplate);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching template:", err);
      setError(err.response?.data?.error || "Failed to load template details");
    }
  };

  const handleSaveTemplate = async (templateId, updateData) => {
    try {
      const authToken = localStorage.getItem("userAccessToken");
      await updateTemplate(authToken, templateId, updateData);

      // Refresh templates list
      await fetchTemplates();

      return true;
    } catch (err) {
      console.error("Error saving template:", err);
      throw err;
    }
  };

  const handleDelete = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        const authToken = localStorage.getItem("userAccessToken");
        await deleteTemplate(authToken, templateId);
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        await fetchTemplates(); // Refresh counts
      } catch (err) {
        console.error("Error deleting template:", err);
        setError(err.response?.data?.error || "Failed to delete template");
      }
    }
  };

  // Duplicate template handler
  const handleDuplicate = async (template) => {
    setDuplicatingId(template.id);
    setError("");

    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // Get full template details
      const response = await getTemplateById(authToken, template.id);
      const fullTemplate = response.data.template || template;

      // Prepare duplicate data - transformDuplicateTemplate will handle the conversion
      const duplicateData = {
        name: `copy_${fullTemplate.name}`,
        category: fullTemplate.category,
        language: fullTemplate.language,
        originalName: fullTemplate.name,
        header_media_url: fullTemplate.header_media_url || null,
        template_json: fullTemplate.template_json, // Pass template_json directly
        waba_id: fullTemplate.waba_id,
        phone_id: fullTemplate.phone_id,
      };

      // Navigate to CreateTemplate - transformDuplicateTemplate will handle the rest
      navigate("/templates/create", {
        state: {
          duplicateTemplate: duplicateData,
        },
      });
    } catch (err) {
      console.error("Error duplicating template:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to duplicate template";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setDuplicatingId(null);
    }
  };

  // Sync templates from Meta
  const handleSyncTemplates = async () => {
    const wabaAccount = JSON.parse(
      localStorage.getItem("selectedWabaAccount") || "{}"
    );
    const wabaId = wabaAccount?.wabaId;
    const phoneId = wabaAccount?.phoneId;

    if (!wabaId || !phoneId) {
      setError("Please select a WhatsApp Business Account first");
      return;
    }

    setIsSyncing(true);
    setError("");
    setSyncSuccess(null);

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await syncTemplatesFromMeta(authToken, wabaId, phoneId);

      if (response.data.success) {
        const stats = response.data.stats;
        setSyncSuccess({
          message: `Synced ${stats.total} templates`,
          details: `${stats.inserted} new, ${stats.updated} updated, ${stats.deleted} removed`,
          duration: stats.duration,
        });

        // Refresh templates list
        await fetchTemplates();

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSyncSuccess(null), 5000);
      }
    } catch (err) {
      console.error("Error syncing templates:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to sync templates from Meta";
      setError(errorMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      text: MessageSquare,
      image: ImageIcon,
      document: FileText,
      buttons: Layout,
      list: List,
    };
    return icons[type] || MessageSquare;
  };

  const getStatusBadge = (status) => {
    if (!status) return {
      icon: Edit,
      label: "DRAFT",
      class: "bg-gray-50 text-gray-700 border-gray-200",
    };
    
    // Normalize status: convert to string, lowercase, and trim whitespace
    const normalizedStatus = String(status).toLowerCase().trim();
    
    const configs = {
      approved: {
        icon: CheckCircle,
        label: "APPROVED",
        class: "border-green-300",
        style: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
      },
      pending: {
        icon: Clock,
        label: "PENDING",
        class: "bg-yellow-100 text-yellow-800 border-yellow-300",
        style: null,
      },
      rejected: {
        icon: XCircle,
        label: "REJECTED",
        class: "bg-red-100 text-red-800 border-red-300",
        style: null,
      },
      paused: {
        icon: Pause,
        label: "PAUSED",
        class: "bg-orange-100 text-orange-800 border-orange-300",
        style: null,
      },
      disabled: {
        icon: Ban,
        label: "DISABLED",
        class: "bg-red-100 text-red-800 border-red-300",
        style: null,
      },
      draft: {
        icon: Edit,
        label: "DRAFT",
        class: "bg-gray-100 text-gray-800 border-gray-300",
        style: null,
      },
    };
    
    const result = configs[normalizedStatus] || configs.draft;
    return result;
  };

  const getQualityBadge = (quality) => {
    if (!quality) return {
      label: "UNKNOWN",
      class: "bg-gray-100 text-gray-800 border-gray-300",
    };
    
    const normalizedQuality = String(quality).toUpperCase().trim();
    
    const configs = {
      GREEN: {
        label: "GREEN",
        class: "bg-green-100 text-green-800 border-green-300",
      },
      YELLOW: {
        label: "YELLOW",
        class: "bg-yellow-100 text-yellow-800 border-yellow-300",
      },
      RED: {
        label: "RED",
        class: "bg-red-100 text-red-800 border-red-300",
      },
      UNKNOWN: {
        label: "UNKNOWN",
        class: "bg-gray-100 text-gray-800 border-gray-300",
      },
    };
    
    return configs[normalizedQuality] || configs.UNKNOWN;
  };

  // Get unique languages, statuses, and categories from current page templates for filter dropdowns
  const uniqueLanguages = [...new Set(templates.map(t => t.language).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(templates.map(t => t.status).filter(Boolean))].sort();
  const uniqueCategories = [...new Set(templates.map(t => t.category).filter(Boolean))].sort();

  // Calculate pagination info
  const totalPages = Math.ceil(totalTemplates / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalTemplates);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 py-4">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncTemplates}
                disabled={isSyncing}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all shadow-sm border ${
                  isSyncing
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
                title="Sync templates from Meta"
              >
                {isSyncing ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    <span className="hidden sm:inline">Syncing...</span>
                  </>
                ) : (
                  <>
                    <CloudDownload size={16} />
                    <span className="hidden sm:inline">Sync from Meta</span>
                  </>
                )}
              </button>
              <button
                onClick={() => navigate("/templates/create")}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg transition-all shadow-sm"
              >
                <Plus size={16} />
                New Template
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-600 mb-0.5">
                    Total Templates
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {totalTemplates}
                  </p>
                </div>
                <div className="p-2 bg-blue-50 rounded-lg">
                  <MessageSquare className="text-blue-600" size={18} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-600 mb-0.5">Approved</p>
                  <p className="text-xl font-bold text-green-600">
                    {counts.approved || 0}
                  </p>
                </div>
                <div className="p-2 bg-green-50 rounded-lg">
                  <CheckCircle className="text-green-600" size={18} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-600 mb-0.5">Pending</p>
                  <p className="text-xl font-bold text-yellow-600">
                    {counts.pending || 0}
                  </p>
                </div>
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <Clock className="text-yellow-600" size={18} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
          >
            <AlertCircle className="text-red-600" size={16} />
            <div className="flex-1">
              <p className="text-xs text-red-700 font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800"
            >
              <XCircle size={16} />
            </button>
          </motion.div>
        )}

        {/* Sync Success Alert */}
        {syncSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2"
          >
            <CheckCircle className="text-green-600 flex-shrink-0" size={16} />
            <div className="flex-1">
              <p className="text-xs text-green-700 font-medium">
                {syncSuccess.message}
              </p>
              <p className="text-[10px] text-green-600 mt-0.5">
                {syncSuccess.details} • Completed in {(syncSuccess.duration / 1000).toFixed(1)}s
              </p>
            </div>
            <button
              onClick={() => setSyncSuccess(null)}
              className="text-green-600 hover:text-green-800"
            >
              <XCircle size={16} />
            </button>
          </motion.div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 relative">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white min-w-[130px]"
            >
              <option value="all">All Status</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={filterLanguage}
              onChange={(e) => setFilterLanguage(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white min-w-[130px]"
            >
              <option value="all">All Languages</option>
              {uniqueLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white min-w-[130px]"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map((category) => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader
                size={32}
                className="animate-spin text-[#25D366] mx-auto mb-3"
              />
              <p className="text-sm text-gray-600">Loading templates...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && templates.length === 0 && (
          <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="text-gray-400" size={24} />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1.5">
                No templates yet
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Create your first message template to start broadcasting
              </p>
              <button
                onClick={() => navigate("/templates/create")}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg transition-all"
              >
                <Plus size={16} />
                Create Your First Template
              </button>
            </div>
          </div>
        )}

        {/* Templates Table */}
        {!isLoading && templates.length > 0 && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      SR.No
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      Template Name
                    </th>
                   
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      Category
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      Language
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      Status
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      Quality
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                      Created
                    </th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-gray-600 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {templates.map((template, index) => {
                  const Icon = getTypeIcon(template.template_type);
                  const statusConfig = getStatusBadge(template.status);
                  const StatusIcon = statusConfig.icon;
                  const qualityConfig = getQualityBadge(template.quality_score);

                  return (
                    <tr
                      key={template.id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-3 py-3">
                        <span className="text-xs text-gray-600 font-medium">
                          {startIndex + index}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {template.name}
                          </p>
                          {/* Show rejection reason */}
                          {template.status?.toLowerCase() === "rejected" &&
                            template.rejected_reason && (
                              <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                                <AlertCircle size={10} />
                                {template.rejected_reason}
                              </p>
                            )}
                        </div>
                      </td>
                     
                      <td className="px-3 py-3">
                        <span className="text-xs text-gray-700 capitalize">
                          {template.category.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-gray-700 uppercase">
                          {template.language || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusConfig.class || 'bg-gray-50 text-gray-700 border-gray-200'}`}
                          style={statusConfig.style || {}}
                        >
                          <StatusIcon size={12} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${qualityConfig.class}`}
                        >
                          {qualityConfig.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">
                        {new Date(template.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Refresh Status Button */}
                          {template.template_id &&
                            (template.status?.toLowerCase() === "pending" ||
                              template.status?.toLowerCase() === "rejected") && (
                              <button
                                onClick={() => handleRefreshStatus(template)}
                                disabled={refreshingId === template.id}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Refresh status from Meta"
                              >
                                <RefreshCw
                                  size={16}
                                  className={
                                    refreshingId === template.id
                                      ? "animate-spin"
                                      : ""
                                  }
                                />
                              </button>
                            )}

                          <button
                            onClick={() => handleDuplicate(template)}
                            disabled={duplicatingId === template.id}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Duplicate template"
                          >
                            {duplicatingId === template.id ? (
                              <Loader size={16} className="animate-spin" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => handleViewEdit(template)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View/Edit template"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete template"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 bg-white rounded-lg border border-gray-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Showing <span className="font-semibold text-gray-900">{startIndex}</span> to{" "}
                  <span className="font-semibold text-gray-900">{endIndex}</span> of{" "}
                  <span className="font-semibold text-gray-900">{totalTemplates}</span> templates
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Previous button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? "bg-[#25D366] text-white"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                {/* Next button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </motion.div>
          )}
        </>
        )}
      </div>

      {/* View Modal */}
      {selectedTemplate && (
        <ViewEditTemplateModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
      )}
    </div>
  );
};

export default Templates;
